import { useState, type CSSProperties, type FormEvent } from 'react';
import { Bug } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { submitFeedback } from '../api/client';

/**
 * Report a Sabrina bug without leaving Sabrina.
 *
 * Staff used to report these by telling Ben, who filed the GitHub issue by
 * hand. The page the reporter is on is captured automatically — it is the
 * question that otherwise always has to be asked back.
 */
interface SubmitResult {
  filed: boolean;
  github_url: string | null;
}

export default function ReportBugButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const close = () => {
    setOpen(false);
    setTitle('');
    setBody('');
    setError(null);
    setResult(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError('Give it a short title and say what happened.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const page = `${location.pathname}${location.search}`;
      const response = await submitFeedback({ title: title.trim(), body: body.trim(), page });
      setResult({ filed: response.filed, github_url: response.github_url });
    } catch {
      // Deliberately leaves the form populated. Re-typing a bug report because
      // the first attempt failed is the kind of small insult that stops people
      // reporting bugs at all.
      setError('The report could not be sent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} style={triggerStyle}>
        <Bug size={16} aria-hidden="true" />
        Report a bug
      </button>

      {open && (
        <div style={overlayStyle} role="presentation" onClick={close}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Report a bug"
            style={dialogStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {result ? (
              <>
                <h2 style={headingStyle}>Thanks — that's logged</h2>
                {/* Told either way on purpose. Letting someone believe a bug was
                    filed when it only reached our database would be worse than
                    saying plainly that it needs picking up. */}
                <p style={bodyTextStyle}>
                  {result.filed
                    ? 'Your report has been filed and will show up in the bug list.'
                    : 'Your report is saved, but it could not be filed to the bug list automatically, so it will need to be picked up manually.'}
                </p>
                {result.github_url && (
                  <p style={bodyTextStyle}>
                    <a href={result.github_url} target="_blank" rel="noreferrer">View the issue</a>
                  </p>
                )}
                <div style={actionsStyle}>
                  <button type="button" onClick={close} style={primaryButtonStyle}>Close</button>
                </div>
              </>
            ) : (
              <form onSubmit={submit} noValidate>
                <h2 style={headingStyle}>Report a bug</h2>
                <p style={bodyTextStyle}>
                  You're on <code>{location.pathname}{location.search}</code> — that gets included
                  automatically.
                </p>
                <label style={labelStyle}>
                  What's wrong?
                  <input
                    aria-label="What's wrong?"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={submitting}
                    placeholder="Short summary"
                    style={inputStyle}
                  />
                </label>
                <label style={labelStyle}>
                  What happened?
                  <textarea
                    aria-label="What happened?"
                    rows={6}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    disabled={submitting}
                    placeholder="What you did, what you expected, what you got."
                    style={inputStyle}
                  />
                </label>
                {error && <div role="alert" style={errorStyle}>{error}</div>}
                <div style={actionsStyle}>
                  <button type="button" onClick={close} disabled={submitting} style={secondaryButtonStyle}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                    {submitting ? 'Sending…' : 'Send report'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const triggerStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.45rem', width: '100%',
  padding: '0.55rem 0.7rem', border: '1px solid var(--border)', borderRadius: 8,
  background: 'transparent', color: 'var(--text-muted)', font: 'inherit',
  fontSize: '0.82rem', cursor: 'pointer',
};
const overlayStyle: CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000,
};
const dialogStyle: CSSProperties = {
  width: 'min(520px, 100%)', boxSizing: 'border-box', background: 'var(--card)',
  borderRadius: 10, padding: '1.25rem', boxShadow: '0 20px 45px rgba(0,0,0,0.25)',
};
const headingStyle: CSSProperties = { margin: '0 0 0.4rem', fontSize: '1.1rem' };
const bodyTextStyle: CSSProperties = { margin: '0 0 0.9rem', fontSize: '0.84rem', color: 'var(--text-muted)', lineHeight: 1.45 };
const labelStyle: CSSProperties = { display: 'grid', gap: '0.3rem', marginBottom: '0.8rem', fontSize: '0.82rem', fontWeight: 700 };
const inputStyle: CSSProperties = {
  boxSizing: 'border-box', width: '100%', padding: '0.5rem 0.6rem',
  border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)',
  color: 'var(--text)', font: 'inherit',
};
const actionsStyle: CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.4rem' };
const primaryButtonStyle: CSSProperties = {
  border: 0, borderRadius: 6, padding: '0.5rem 0.85rem', background: 'var(--primary, #1f6feb)',
  color: '#fff', fontWeight: 700, cursor: 'pointer',
};
const secondaryButtonStyle: CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 6, padding: '0.5rem 0.85rem',
  background: 'var(--card)', color: 'var(--text)', fontWeight: 700, cursor: 'pointer',
};
const errorStyle: CSSProperties = { color: 'var(--danger, #d73a49)', fontSize: '0.8rem', marginBottom: '0.6rem' };
